/**
 * Country helpers for the gift card catalogue.
 *
 * <p>Brands carry an ISO 3166-1 alpha-2 country code (e.g. {@code GB}). The display name is derived
 * from the code with {@link Intl.DisplayNames} rather than a hand-maintained table, so the names
 * stay correct and localised without a second list to keep in sync with the backend.
 */

/** Renders a flag emoji from an ISO 3166-1 alpha-2 code, or an empty string when unusable. */
export function countryFlag(code) {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return ''
  // Regional indicator symbols: 'A' (0x41) maps to U+1F1E6.
  return String.fromCodePoint(
    ...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  )
}

/**
 * The English display name for an ISO 3166-1 alpha-2 country code.
 *
 * @param {string|null|undefined} code e.g. "GB"
 * @returns {string} e.g. "United Kingdom", or the raw code when it cannot be resolved
 */
export function countryName(code) {
  if (!code) return ''
  try {
    const display = new Intl.DisplayNames(['en'], { type: 'region' })
    return display.of(code.toUpperCase()) || code
  } catch {
    // Older runtimes without Intl.DisplayNames: the code is still meaningful on its own.
    return code
  }
}

/**
 * A country rendered for display, e.g. "🇬🇧 United Kingdom".
 *
 * <p>Falls back to an em dash when there is no country — the provider's "Global" bucket is a
 * rest-of-world grouping, not a country, and is deliberately left blank rather than guessed at.
 */
export function formatCountry(code) {
  if (!code) return '—'
  const name = countryName(code)
  const flag = countryFlag(code)
  return flag ? `${flag} ${name}` : name
}
